import * as React from 'react'

import { Radio as RadioPrimitive } from '@base-ui/react/radio'
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group'

import { cn } from '@/lib/utils'

/**
 * Visual states (per Figma `STX-Components / Checkbox 24px`, radio variant):
 *
 *   unchecked         — `--input` outline, transparent fill, no inner dot
 *   checked           — `--primary` outline, primary inner dot (Figma 10:618)
 *   disabled unchecked — solid gray fill (`--input` border + bg, no inner dot)
 *                        — visibly distinct from active-unchecked so users
 *                          can see the option is locked.
 *   disabled checked   — `--input` outline + gray inner dot
 *
 * Composition: caller wraps in `<label>` for label text (shadcn convention,
 * mirrors `<Checkbox>` from `./checkbox`).
 *
 *   <RadioGroup value={x} onValueChange={setX}>
 *     <label className="tw:flex tw:items-center tw:gap-2 tw:cursor-pointer">
 *       <Radio value="a" />
 *       <span>Option A</span>
 *     </label>
 *   </RadioGroup>
 */
function RadioGroup({
  className,
  value,
  defaultValue,
  ...props
}: RadioGroupPrimitive.Props) {
  // Match the Checkbox compat: stores often expose `someValue | undefined`
  // for the active radio. Coerce undefined → null so Base UI keeps the
  // group controlled instead of flipping to uncontrolled mode.
  const resolvedValue = defaultValue === undefined ? (value ?? null) : value

  return (
    <RadioGroupPrimitive
      data-slot="radio-group"
      className={cn('tw:flex tw:flex-col tw:gap-2', className)}
      value={resolvedValue}
      defaultValue={defaultValue}
      {...props}
    />
  )
}

function Radio({ className, ...props }: RadioPrimitive.Root.Props) {
  return (
    <RadioPrimitive.Root
      data-slot="radio"
      className={cn(
        'tw:group/radio tw:peer tw:relative tw:inline-flex tw:flex-none tw:basis-[20px] tw:w-[20px] tw:min-w-[20px] tw:max-w-[20px] tw:h-[20px] tw:min-h-[20px] tw:max-h-[20px] tw:items-center tw:justify-center tw:rounded-full tw:border-2 tw:border-input tw:bg-transparent tw:p-0 tw:cursor-pointer tw:outline-none tw:transition-colors',
        'tw:after:absolute tw:after:-inset-1',
        'tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2 tw:focus-visible:ring-offset-background',
        // checked (active) — primary outline + primary dot
        'tw:data-checked:border-primary',
        // disabled — gray-ish pattern (cursor + outline always; fill the
        // ring solid for unchecked so the locked option reads as inactive)
        'tw:data-disabled:cursor-not-allowed',
        'tw:data-disabled:data-checked:border-input',
        'tw:data-disabled:data-unchecked:border-input tw:data-disabled:data-unchecked:bg-input',
        // invalid
        'tw:aria-invalid:border-destructive tw:aria-invalid:ring-2 tw:aria-invalid:ring-destructive/20',
        className,
      )}
      {...props}
    >
      <RadioPrimitive.Indicator
        data-slot="radio-indicator"
        className={cn(
          'tw:flex tw:size-[10px] tw:rounded-full tw:bg-primary tw:transition-colors',
          'tw:group-data-disabled/radio:bg-input',
        )}
      />
    </RadioPrimitive.Root>
  )
}

export { Radio, RadioGroup }
export type RadioProps = RadioPrimitive.Root.Props
export type RadioGroupProps = RadioGroupPrimitive.Props
